import { LitElement, html, css, nothing, type TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import './index'; // registers <lead-card>, <nfc-app-shell>, <nfc-search> + Material Web
import './components/card-extras';
import './components/lang-picker';

/**
 * Everything else is loaded ON DEMAND — see `need()`.
 *
 * The busiest path through this app is a stranger who tapped a physical tag,
 * usually on mobile data, to look at somebody's card. That view renders a card
 * and its extras; it has no use for the editor, the company console, the
 * collect sheet or the stats panel — which together are the largest part of the
 * bundle. Shipping them anyway made the one page that must open instantly the
 * page that carried the whole application.
 *
 * Custom elements upgrade in place, so a tag rendered before its module arrives
 * simply fills in when it does. No placeholder is needed except where the
 * element IS the screen, which is why only the editor reports loading.
 */
import '@material/web/button/filled-button.js';
import '@material/web/button/outlined-button.js';
import '@material/web/iconbutton/icon-button.js';
import '@material/web/fab/fab.js';
import { myCard, collectedCards, publicCard, shareUrl, toProjection, fetchProfiles, type Card } from './lib/pocketbase';
import { listMyCards, setActiveCard, deleteMyCard } from './lib/mycard';
import { industriesIn, industryOf, matches } from './lib/directory';
import { classMap } from 'lit/directives/class-map.js';
import { saveCollected, refreshCollected } from './lib/collected';
import { deleteCard, ApiError } from './lib/api';
import { recordCollect, fetchLegal, type CollectChannel, type LegalNotice } from './lib/api';
import { syncFromAccount } from './lib/sync';
import { signedIn, keepAlive } from './lib/session';
import { icon } from './lib/icons';
import { cardLabel } from './lib/cardkey';
import { downloadVCard } from './lib/vcard';
import { downloadCSV } from './lib/export';
import { type CollectedCard } from './lib/collected';
import { listNotes, saveNote, deleteNote, type Note } from './lib/notes';
import { t, n, intlTag, LocaleController } from './lib/i18n';

type Route = { view: 'app' } | { view: 'profile'; id: string };

/** Below this many collected cards a directory is scannable by eye, and a
 *  filter row would be chrome that never earns its space. */
const SORT_THRESHOLD = 7;

/** When a note was touched, in words. "2h ago" places a young note far better
 *  than a date does; past a week the date is the honest answer. Localized by
 *  Intl, so the Bangla interface gets Bangla words and digits for free. */
function relativeTime(ts: number): string {
  const s = Math.round((Date.now() - ts) / 1000);
  const rtf = new Intl.RelativeTimeFormat(intlTag(), { numeric: 'auto' });
  if (s < 45) return rtf.format(0, 'second');
  if (s < 3600) return rtf.format(-Math.round(s / 60), 'minute');
  if (s < 86400) return rtf.format(-Math.round(s / 3600), 'hour');
  if (s < 7 * 86400) return rtf.format(-Math.round(s / 86400), 'day');
  const then = new Date(ts);
  const sameYear = then.getFullYear() === new Date().getFullYear();
  return then.toLocaleDateString(intlTag(),
    sameYear ? { day: 'numeric', month: 'short' } : { day: 'numeric', month: 'short', year: 'numeric' });
}

/** Group by industry, then alphabetically within it — the point of sorting a
 *  big directory is that the same trade sits together. */
function byIndustryThenName(a: Card, b: Card): number {
  const ai = industryOf(a);
  const bi = industryOf(b);
  if (ai !== bi) return ai.localeCompare(bi);
  return (a.name || '').localeCompare(b.name || '');
}

/**
 * Overlay states live in the hash, not in component state alone.
 *
 * A phone's Back button walks session history. An editor that is only a boolean
 * on this element leaves no history entry, so Back skips past the whole app and
 * leaves the site — losing what the person was typing. Putting the editor in the
 * hash makes Back do the obvious thing: close it and return to the card.
 */
export const EDIT_HASH = '#/edit';
export const NEW_HASH = '#/new';
export const COLLECT_HASH = '#/collect';

/**
 * Where the page is.
 *
 * Your own card, the cards you have collected, and running a company's cards
 * are three different jobs, and stacking them on one scroll asked somebody to
 * meet all three at once — an admin console sitting under a QR code under a
 * card. One destination at a time is quieter, and each is reachable in a tap
 * instead of a scroll.
 */
export type Tab = 'home' | 'cards' | 'work';

export const TAB_HASH: Record<Tab, string> = {
  home: '',
  cards: '#/collected',
  work: '#/work',
};

/** Which destination a hash belongs to. The overlays live inside one: editing
 *  is something you do to your card, collecting is something you do to the
 *  directory, so Back out of an overlay lands where it was opened. */
export function tabFromHash(hash: string): Tab {
  if (hash === TAB_HASH.work) return 'work';
  if (hash === TAB_HASH.cards) return 'cards';
  return 'home';
}

/** Which overlay a hash asks for. Split out from the element so the mapping —
 *  the thing that decides whether Back works — can be asserted directly. */
export function overlayFromHash(hash: string): { editor: 'edit' | 'new' | null; collecting: boolean } {
  return {
    editor: hash === EDIT_HASH ? 'edit' : hash === NEW_HASH ? 'new' : null,
    collecting: hash === COLLECT_HASH,
  };
}

// A public id is Base62, 6–32 chars (see internal/shortid + platform/config bounds).
const PUBLIC_ID_RE = /^[A-Za-z0-9]{6,32}$/;

/**
 * The router understands two URL shapes:
 *   - the PATH-based public card URL — the shortest NFC tag URL, e.g.
 *     `https://<short-domain>/<publicId>` — which the single binary serves via
 *     SPA fallback (docs/SINGLE-BINARY.md); the PWA reads the id from the path.
 *   - the app's own tabs via the hash (`#/collected`), so tab switches never
 *     touch the path and can't be mistaken for a public id.
 */
function parseRoute(): Route {
  const path = location.pathname.replace(/^\/+|\/+$/g, '');
  if (path && PUBLIC_ID_RE.test(path)) {
    return { view: 'profile', id: path };
  }
  return { view: 'app' };
}

/**
 * <bc-app> — the whole PWA. Hash-routed:
 *   #/my-card  (default) · #/collected · #/p/:id (public receiver view, no chrome)
 * Data comes from PocketBase (src/lib/pocketbase.ts) with demo/offline fallback.
 */
/** What the "keep your cards" slot on a tapped card should show.
 *
 *  Separated from the template so it can be asserted directly rather than
 *  restated in a test — a test that mirrors the render passes even when the
 *  render is wrong. Same reason blockAdminUI is its own function server-side.
 *
 *  The rule: a signed-in person sees their COLLECTION (their own card is
 *  already a destination in the nav, so spending this space on it would waste
 *  it), and only if they have one. Everybody else sees the field that turns
 *  "saved to this browser" into "saved to you" — first the number, then the
 *  code once one has been sent. */
export type KeepView = 'none' | 'collection' | 'number' | 'code';
export function keepView(o: {
  loaded: boolean; signedIn: boolean; collected: number; otpId: string;
}): KeepView {
  if (!o.loaded) return 'none';
  if (o.signedIn) return o.collected > 0 ? 'collection' : 'none';
  return o.otpId ? 'code' : 'number';
}

@customElement('bc-app')
export class BcApp extends LitElement {
  @state() private route: Route = parseRoute();
  @state() private my: Card | null = null;
  @state() private collected: Card[] = [];
  @state() private mine: Card[] = [];
  // Which industry the directory is filtered to ('' = all).
  @state() private industry = '';
  // Which editor is open, derived from the hash so it is part of history.
  // 'edit' opens on the active card; 'new' opens empty.
  @state() private editorMode: 'edit' | 'new' | null = null;
  @state() private profile: Card | null = null;
  @state() private query = '';
  @state() private collecting = false; // the "collect a card" overlay is open
  // Whether THIS app pushed the entry the overlay sits on, so closing knows
  // whether going back is safe or whether the person deep-linked straight in.
  private pushedOverlay = false;
  // Set when someone asks for the company section before they have an identity.
  // The detour through making and confirming a card has to end where they were
  // going, or it reads as the button having done nothing.
  @state() private headingForWork = false;
  @state() private tab: Tab = 'home';
  @state() private legal: LegalNotice = { configured: false };
  @state() private loaded = false; // My Card data fetched — distinguishes "loading" from "no card yet"
  @state() private justCollected = false; // the tapped card was added on this visit
  // Public id of the card being claimed, or '' when the sheet is closed. Set
  // both by "this is my card" on a received card and automatically right after
  // someone creates their own.
  @state() private claimingCardId = '';
  // Distinguishes "you just made this" from "you say this one is yours".
  @state() private claimIsNew = false;
  // What the server said to do after collecting. Only 'external' may ever
  // produce an outbound message, and only from the user's own device.
  @state() private channel: CollectChannel = 'none';

  // Which on-demand components have arrived. Separate booleans rather than a
  // Set, so Lit sees each as a reactive change.
  @state() private editorReady = false;
  @state() private workReady = false;
  @state() private collectReady = false;
  @state() private statsReady = false;
  @state() private shareReady = false;
  @state() private reportReady = false;
  /** Which card the report sheet is open for, or '' for none. */
  @state() private reporting = '';
  @state() private claimReady = false;

  /** Load a component the current view needs, once. */
  private need(what: 'editor' | 'work' | 'collect' | 'stats' | 'share' | 'claim' | 'report') {
    switch (what) {
      case 'editor':
        if (this.editorReady) return;
        void import('./components/creator-flow').then(() => { this.editorReady = true; });
        return;
      case 'work':
        if (this.workReady) return;
        void import('./components/org-panel').then(() => { this.workReady = true; });
        return;
      case 'collect':
        if (this.collectReady) return;
        void import('./components/collect-flow').then(() => { this.collectReady = true; });
        return;
      case 'stats':
        if (this.statsReady) return;
        void import('./components/card-stats').then(() => { this.statsReady = true; });
        return;
      case 'share':
        if (this.shareReady) return;
        void import('./components/share-card').then(() => { this.shareReady = true; });
        return;
      case 'report':
        if (this.reportReady) return;
        void import('./components/report-sheet').then(() => { this.reportReady = true; });
        return;
      case 'claim':
        if (this.claimReady) return;
        void import('./components/claim-sheet').then(() => { this.claimReady = true; });
        return;
    }
  }

  // Re-renders this element when the interface language changes.
  readonly i18n = new LocaleController(this);

  static styles = css`
    :host { display:block; height:100dvh; background:var(--app-bg); color:var(--app-text); font-family:var(--font-plain,system-ui,sans-serif); }

    /* ── One page ────────────────────────────────────────────────────
       Your cards, then the directory. Sections are separated by a rule and
       generous space rather than by tabs: it is one subject, and you move
       between the two constantly. */
    .page { display:flex; flex-direction:column; }
    section { padding:16px 16px 24px; display:flex; flex-direction:column; gap:14px; }
    /* Every interface starts clean at the very top, like a native screen —
       no rule, no heading; whatever is needed lives in the FAB menu. */
    .directory, .work { padding-top:16px; }
    .mine.editing { padding:0; }

    /* Chips do double duty: switching between your own cards, and filtering the
       directory. Same affordance, so the pressed state means the same thing. */
    .switch, .filters { display:flex; gap:7px; flex-wrap:wrap; }
    .chip { cursor:pointer; border:1px solid var(--app-outline,#2a2f3a); background:var(--app-surface,#16181f);
      color:var(--app-text); border-radius:999px; padding:7px 13px; font:inherit; font-size:12.5px; font-weight:600;
      max-width:180px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;
      transition:border-color var(--dur-2,180ms), background var(--dur-2,180ms), color var(--dur-2,180ms); }
    .chip:hover { border-color:var(--app-primary,#1b7a52); }
    .chip.on { background:var(--app-primary,#1b7a52); border-color:transparent; color:var(--app-on-primary,#04120b); }
    .chip.add { display:inline-flex; align-items:center; padding:7px 10px; border-style:dashed; color:var(--app-muted); }

    /* Two destination controls share the leading slot. min-width:0 lets them
       shrink and ellipsis their labels instead of pushing the language picker
       off a narrow phone. */
    .dests { display:flex; align-items:center; gap:8px; min-width:0; }

    /* The other destination, top-left, labelled with where it goes. */
    .dest { display:inline-flex; align-items:center; gap:7px; cursor:pointer; min-width:0;
      background:transparent; border:1px solid var(--app-outline,#2a2f3a); border-radius:999px;
      color:var(--app-text); font:inherit; font-size:13px; font-weight:600; padding:7px 13px;
      transition:border-color var(--dur-2,180ms), background var(--dur-2,180ms); }
    .dest:hover { border-color:var(--app-primary,#1b7a52); }
    .dest svg { width:17px; height:17px; flex:none; color:var(--app-primary,#34d399); }
    /* The same arrow, pointed the other way, when it is a way back. */
    .dest svg.back { transform:rotate(180deg); }
    .dest span { overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }

    /* Delete is the middle of three, so what it targets has to be unambiguous:
       the card itself is ringed, the line runs from it down to the button, and
       the note names it. With a deck of five, a bare "delete" is a question.

       The ring is the part that carries the answer — a line and a caption below
       the deck still leave you looking back and forth to work out which card
       they belong to, and on a phone the card and the buttons are rarely both
       fully in view. */
    .targeted { position:relative; border-radius:20px;
      outline:2px solid var(--app-error,#ff6b6b); outline-offset:5px;
      box-shadow:0 0 0 9px color-mix(in srgb,var(--app-error,#ff6b6b) 13%, transparent);
      animation:targetIn var(--dur-2,180ms) ease-out both; }
    @keyframes targetIn {
      from { outline-color:color-mix(in srgb,var(--app-error,#ff6b6b) 0%, transparent); box-shadow:0 0 0 0 transparent; }
    }
    /* Everything that is NOT the target steps well back, so the deck reads as
       one card in question rather than five cards with a mark on one. */
    .deck.targeting .slide:not(.targeted) { opacity:.22; filter:grayscale(.6); }
    .deck.targeting { scroll-snap-type:none; } /* nothing to swipe to mid-answer */

    /* The question opens as a HEIGHT, not as content appearing from nowhere:
       the row above it never moves, and the page below slides rather than
       jumping. Same 0fr↔1fr grid the editor's fill panel uses. */
    .delslot { display:grid; grid-template-rows:0fr;
      transition:grid-template-rows var(--dur-3,260ms) var(--ease-emphasized-decelerate,cubic-bezier(.05,.7,.1,1)); }
    .delslot.open { grid-template-rows:1fr; }
    .delinner { overflow:hidden; min-height:0; opacity:0;
      transition:opacity var(--dur-2,180ms) ease; }
    .delslot.open .delinner { opacity:1; }
    /* The column separates its children with a 15px gap, and a collapsed slot
       would still take one — an empty step under the buttons that nothing
       explains. Cancel it here and pay it back inside the box, where it
       animates with the height instead of sitting there permanently. */
    .delslot { margin-top:-15px; }
    .delslot .delbox { margin-top:15px; }

    .deldone { display:flex; align-items:center; justify-content:center; gap:7px; margin:0;
      font-size:12.5px; font-weight:600; color:var(--app-muted);
      animation:doneIn var(--dur-2,180ms) ease-out both; }
    .deldone svg { width:15px; height:15px; flex:none; color:var(--app-primary,#34d399); }
    @keyframes doneIn { from { opacity:0; transform:translateY(-3px); } }

    @media (prefers-reduced-motion:reduce){
      .targeted, .deldone { animation:none; }
      .delslot, .delinner { transition:none; }
    }
    /* Every state, not just at rest: Material keeps separate hover, focus and
       pressed colours that each fall back to the primary green, so setting only
       the base leaves the button turning green the moment it is touched — which
       is exactly when it most needs to look destructive. */
    .cardacts .del {
      --md-text-button-label-text-color:var(--app-error,#ff6b6b);
      --md-text-button-hover-label-text-color:var(--app-error,#ff6b6b);
      --md-text-button-focus-label-text-color:var(--app-error,#ff6b6b);
      --md-text-button-pressed-label-text-color:var(--app-error,#ff6b6b);
      --md-text-button-icon-color:var(--app-error,#ff6b6b);
      --md-text-button-hover-icon-color:var(--app-error,#ff6b6b);
      --md-text-button-focus-icon-color:var(--app-error,#ff6b6b);
      --md-text-button-pressed-icon-color:var(--app-error,#ff6b6b);
      --md-text-button-hover-state-layer-color:var(--app-error,#ff6b6b);
      --md-text-button-pressed-state-layer-color:var(--app-error,#ff6b6b);
    }
    /* While the confirmation is open the safe actions step back AND stop
       working: they were only dimmed before, so "New card" was still live under
       a question about deleting, and pressing it walked into the editor with the
       question left open behind it. */
    .cardacts.targeting .primary,
    .cardacts.targeting .minoracts md-text-button:not(.del) { opacity:.4; }

    .delbox { display:flex; flex-direction:column; gap:8px; padding:14px; border-radius:14px;
      background:color-mix(in srgb,var(--app-error,#ff6b6b) 11%, transparent);
      border:1px solid color-mix(in srgb,var(--app-error,#ff6b6b) 34%, transparent); }
    .del-h { display:flex; align-items:center; gap:8px; font-size:14px; font-weight:700;
      color:var(--app-error,#ff6b6b); }
    .del-h svg { width:17px; height:17px; flex:none; }
    .del-b { margin:0; font-size:13px; line-height:1.5; color:var(--app-muted); }
    .del-b.strong { color:var(--app-error,#ff6b6b); font-weight:600; }
    /* A refusal is stated here, next to the button that was pressed. Silence
       after pressing Delete reads as a broken button, which is exactly what it
       looked like. */
    .del-err { display:flex; align-items:flex-start; gap:7px; margin:0; font-size:12.5px;
      line-height:1.45; font-weight:600; color:var(--app-error,#ff6b6b); }
    .del-err svg { width:15px; height:15px; flex:none; margin-top:1px; }
    .del-a { display:flex; gap:8px; justify-content:flex-end; align-items:center; }
    /* Dark label on the light red, not white: white on #ff6b6b is about 2.5:1
       and unreadable, and the rest of the app already pairs a light container
       with dark text. */
    .del-a md-filled-button { --md-filled-button-container-color:var(--app-error,#ff6b6b);
      --md-filled-button-label-text-color:#2a0707;
      --md-filled-button-hover-label-text-color:#2a0707;
      --md-filled-button-focus-label-text-color:#2a0707;
      --md-filled-button-pressed-label-text-color:#2a0707; }
    .orggate { display:flex; flex-direction:column; gap:10px; padding:16px; border-radius:16px;
      background:var(--app-surface,#16181f); border:1px solid var(--app-outline,#2a2f3a); }
    .orggate p { margin:0; font-size:13.5px; line-height:1.55; color:var(--app-text); }
    .orggate .hint { color:var(--app-muted); }
    .orggate md-filled-button { margin-top:2px; --md-filled-button-container-height:46px; }

    /* ── More than one card of your own ──────────────────────────────
       A native horizontal scroll with snap points. The slide is narrower than
       the track on purpose: the next card shows at the edge, which is what
       tells you there IS a next card without a word of explanation. */
    .deck { display:flex; gap:12px; overflow-x:auto; overflow-y:hidden;
      scroll-snap-type:x mandatory; scrollbar-width:none;
      /* Bleed to the section edges so a card can sit centred with its
         neighbours peeking on both sides. */
      margin:0 -16px; padding:0 16px; scroll-padding:0 16px; }
    .deck::-webkit-scrollbar { display:none; }
    .slide { flex:0 0 88%; scroll-snap-align:start; min-width:0;
      transition:opacity var(--dur-2,180ms); }
    /* The one you are on is the one in focus; the peek is a hint, not a rival. */
    .slide[aria-current="false"] { opacity:.55; }

    .deckbar { display:flex; align-items:center; gap:12px; }
    .dots { display:flex; align-items:center; gap:7px; flex:1; }
    .dot { width:7px; height:7px; padding:0; border:none; border-radius:999px; cursor:pointer;
      background:var(--app-outline-strong,#39404e);
      transition:background var(--dur-2,180ms), width var(--dur-2,180ms); }
    .dot.on { width:20px; background:var(--app-primary,#34d399); }
    .deckcount { font:600 11px/1 var(--font-mono,ui-monospace,monospace);
      letter-spacing:calc(.06em * var(--track,1)); color:var(--app-faint); }

    @media (min-width:900px){
      /* With room, show them side by side instead of one at a time. */
      .deck { margin:0; padding:0; }
      .slide { flex:0 0 clamp(300px, 46%, 420px); }
    }

    .minegrid { display:flex; flex-direction:column; gap:15px; }
    /* Nothing between the app bar and the card: the card is the subject of the
       page, and a heading above it only pushed it down the screen. */
    .mine { padding-top:14px; }
    /* One quiet row of standard small buttons, held a clear step below the
       card so none of them reads as part of it (operator decision,
       2026-08-29). Edit keeps the filled emphasis; it is just no longer a
       bar the width of the card. */
    .cardacts { display:flex; align-items:center; justify-content:center; gap:10px;
      flex-wrap:wrap; margin-top:10px; }
    .cardacts .primary { --md-filled-button-container-height:36px;
      --md-filled-button-label-text-size:13px; }
    .minoracts { display:flex; align-items:center; gap:10px; }
    .minoracts md-text-button { --md-text-button-container-height:36px;
      --md-text-button-label-text-size:13px; }
    /* The tap figures, hairline-separated from the actions above them so they
       read as information about the card rather than another control. */
    .taps { margin-top:4px; padding-top:14px; border-top:1px solid var(--app-outline); }
    .pad { padding:16px 16px 26px; display:flex; flex-direction:column; gap:15px; }
    .ov { font:600 10px/1 var(--font-mono,ui-monospace,monospace); letter-spacing:calc(.16em * var(--track, 1)); text-transform:uppercase; color:var(--app-faint); }
    .share { display:flex; gap:10px; } .share > * { flex:1; }
    .list { display:flex; flex-direction:column; gap:14px; }
    /* The one FAB and its M3 FAB menu (m3.material.io/components/fab-menu).
       The list gets breathing room underneath so the FAB never sits on the
       last card. */
    .page { padding-bottom:96px; }
    /* The install banner owns the very bottom edge; the buttons sit just
       above it (its measured height is --install-bar-h, which already carries
       a 24px allowance) and drop back to the edge once it is gone. */
    /* As low as the edge (or the install banner / keyboard) allows, so the
       cards above get every pixel the chrome does not need. */
    .dirfab { position:fixed; right:16px;
      bottom:calc(var(--safe-bottom,0px) + max(8px, var(--install-bar-h,0px)));
      z-index:56; width:56px; height:56px; border-radius:18px; border:none; cursor:pointer;
      display:flex; align-items:center; justify-content:center; color:var(--app-on-primary,#381e72);
      background:var(--app-primary,#d0bcff); box-shadow:0 6px 20px -4px rgba(0,0,0,.45); }
    .dirfab svg { width:24px; height:24px; transition:transform var(--dur-2,180ms) ease; }
    .dirfab.open svg { transform:rotate(90deg); }
    .dirfab.filtered::after { content:''; position:absolute; top:10px; right:10px; width:9px; height:9px;
      border-radius:50%; background:var(--app-error,#ff6b6b); }
    /* On the directory, search is zero taps and zero chrome: the writable
       field itself lives on the FAB's line, filtering as you type — no button,
       no sheet, nothing to open or dismiss. */
    .searchbar { position:fixed; left:16px; right:84px;
      bottom:calc(var(--safe-bottom,0px) + max(8px, var(--install-bar-h,0px)) + 4px);
      z-index:53; filter:drop-shadow(0 4px 14px rgba(0,0,0,.4)); }
    /* Open, the page scrims and the actions stack above the FAB as icon+label
       pills, staggered upward out of the button — the FAB itself turns into
       the close control, exactly the component's own choreography. */
    .fab-scrim { position:fixed; inset:0; z-index:54; border:none; padding:0; cursor:default;
      background:color-mix(in srgb, var(--app-bg,#141218) 58%, transparent);
      -webkit-backdrop-filter:blur(2px); backdrop-filter:blur(2px);
      animation:ovIn var(--dur-2,180ms) ease both; }
    .fabmenu { position:fixed; right:16px;
      bottom:calc(var(--safe-bottom,0px) + max(8px, var(--install-bar-h,0px)) + 68px);
      z-index:55; display:flex; flex-direction:column; align-items:flex-end; gap:8px; }
    .fmi { display:inline-flex; align-items:center; gap:10px; height:48px; padding:0 18px;
      border:none; border-radius:999px; cursor:pointer; font:inherit; font-size:14px; font-weight:600;
      color:var(--app-on-primary-container,#B4F1D2); background:var(--app-primary-container,#1F5540);
      box-shadow:0 3px 12px -2px rgba(0,0,0,.4);
      animation:fmiIn var(--dur-3,260ms) var(--ease-emphasized-decelerate,cubic-bezier(.05,.7,.1,1)) backwards; }
    .fmi svg { width:22px; height:22px; }
    .fmi.on { background:var(--app-primary,#1b7a52); color:var(--app-on-primary,#04120b); }
    /* Nearest the FAB lands first, so the stack reads as growing out of it. */
    .fabmenu > :nth-last-child(2) { animation-delay:30ms; }
    .fabmenu > :nth-last-child(3) { animation-delay:60ms; }
    .fabmenu > :nth-last-child(4) { animation-delay:90ms; }
    .fabmenu > :nth-last-child(5) { animation-delay:120ms; }
    .fabmenu > :nth-last-child(6) { animation-delay:150ms; }
    .fabmenu > :nth-last-child(7) { animation-delay:180ms; }
    .fabmenu > :nth-last-child(8) { animation-delay:210ms; }
    .fabmenu lang-picker { animation:fmiIn var(--dur-3,260ms) var(--ease-emphasized-decelerate,cubic-bezier(.05,.7,.1,1)) backwards; }
    @keyframes fmiIn { from{ opacity:0; transform:translateY(12px) scale(.9); } to{ opacity:1; transform:none; } }
    /* The notes interface (FAB menu → Notes): a plain notes list. Write one,
       tap one to change or delete it — simple CRUD, unlinked from cards. */
    .notebtn { display:flex; align-items:center; gap:8px; width:100%; text-align:left;
      cursor:pointer; background:transparent; border:none; padding:10px 4px; font:inherit;
      font-size:13.5px; color:var(--app-faint,#6b7280); }
    .notebtn:hover { color:var(--app-muted); }
    .notebtn svg { width:16px; height:16px; flex:none; opacity:.75; }
    .noteedit { display:flex; flex-direction:column; gap:4px; padding:2px 0 0; }
    .noteinput { width:100%; box-sizing:border-box; resize:vertical; font:inherit; font-size:13.5px;
      line-height:1.5; color:var(--app-text); background:var(--app-surface,#16181f);
      border:1px solid var(--app-outline-strong,#39404e); border-radius:10px; padding:9px 11px; }
    .noteinput:focus { outline:none; border-color:var(--app-primary,#1b7a52); }
    .noteacts { display:flex; justify-content:flex-end; gap:2px; }
    .noteacts .notedel { margin-right:auto; --md-text-button-label-text-color:var(--app-error,#ff6b6b); }
    .noteent { display:flex; flex-direction:column; padding:2px 0;
      border-bottom:1px solid var(--app-outline,#2a2f3a); }
    .noteent:last-child { border-bottom:none; }
    .noteitem { display:flex; flex-direction:column; gap:4px; width:100%; text-align:left;
      cursor:pointer; background:transparent; border:none; padding:12px 4px; font:inherit; }
    .noteitem:hover .notetext { color:var(--app-text); }
    .notetext { font-size:13.5px; line-height:1.5; color:var(--app-muted);
      white-space:pre-wrap; word-break:break-word;
      overflow:hidden; display:-webkit-box; -webkit-box-orient:vertical; -webkit-line-clamp:4; }
    .notedate { font:500 11px/1 var(--font-mono,ui-monospace,monospace); color:var(--app-faint,#6b7280); }
    .list > * { animation:cardIn var(--dur-3,260ms) var(--ease-emphasized-decelerate,cubic-bezier(.05,.7,.1,1)) backwards; }
    .fab { position:sticky; bottom:16px; display:flex; justify-content:flex-end; pointer-events:none; }
    .collect-overlay { position:fixed; inset:0; z-index:60; display:flex; align-items:flex-end; justify-content:center;
      padding:0 12px calc(env(safe-area-inset-bottom,0) + 16px); background:rgba(4,6,10,.55);
      -webkit-backdrop-filter:blur(3px); backdrop-filter:blur(3px); animation:ovIn .2s ease both; }
    .collect-overlay > collect-flow { width:100%; max-width:460px; }
    @media (min-width:520px){ .collect-overlay{ align-items:center; } }
    @keyframes ovIn { from{ opacity:0; } to{ opacity:1; } }
    .fab md-fab { pointer-events:auto; }
    .empty { text-align:center; color:var(--app-muted); padding:40px 20px; font-size:14px; }
    @keyframes cardIn { from{ opacity:0; transform:translateY(14px); } to{ opacity:1; transform:none; } }
    /* public profile (receiver) */
    .checkin-note { display:flex; align-items:center; justify-content:center; gap:6px;
      font-size:12px; color:var(--app-muted,#938f99); margin-top:2px; text-align:center; }
    .report-link { display:inline-flex; align-items:center; gap:6px; margin:4px auto 0; padding:6px 10px;
      font:inherit; font-size:12px; color:var(--app-muted,#938f99); background:none; border:none;
      cursor:pointer; opacity:.8; }
    .report-link:hover { opacity:1; text-decoration:underline; }
    .pp { min-height:100dvh; display:flex; flex-direction:column; gap:18px; padding:16px 18px calc(env(safe-area-inset-bottom,0) + 20px); max-width:460px; margin:0 auto; }
    .brand { display:flex; align-items:center; gap:8px; font-family:var(--font-brand,system-ui); font-weight:600; font-size:15px; }
    .brand .dot { width:18px; height:18px; border-radius:6px; background:linear-gradient(135deg,#3cae7c,#7fe0b0); }
    .brand em { margin-left:auto; font-style:normal; font:400 10px/1 var(--font-mono,ui-monospace,monospace); letter-spacing:calc(.14em * var(--track, 1)); text-transform:uppercase; color:var(--app-faint); }
    .ppacts { display:flex; flex-direction:column; gap:10px; }
    .ppacts md-filled-button { width:100%; --md-filled-button-container-height:52px; }
    .ppacts .mine { width:100%; --md-outlined-button-container-height:50px; }
    .ppacts .invite { width:100%; }
    .split { display:flex; gap:10px; } .split > * { flex:1; --md-outlined-button-container-height:48px; }
    .cta { margin-top:auto; text-align:center; color:var(--app-muted); font-size:12px; display:flex; flex-direction:column; gap:12px; align-items:center; }
    .saved { display:flex; align-items:center; gap:8px; font-size:13px; font-weight:600;
      color:var(--app-primary,#34d399); }
    .saved a { color:var(--app-muted); font-weight:500; }
    .tap-hint { display:flex; align-items:center; justify-content:center; gap:6px;
      font-size:12px; color:var(--app-muted); margin-top:-6px; }
    .tap-hint .ico, .tap-hint svg { font-size:15px; width:15px; height:15px; }
    .legal { margin-top:22px; padding-top:14px; font-size:11.5px; line-height:1.6;
      color:var(--app-muted); text-align:center;
      border-top:1px solid color-mix(in srgb, currentColor 12%, transparent); }
    .legal-who { font-weight:600; }
    .legal-line { opacity:.85; }
    .legal-links { display:flex; gap:14px; justify-content:center; flex-wrap:wrap; margin-top:6px; }
    .legal-links a { color:inherit; text-decoration:underline; text-underline-offset:2px; }
    .keep { display:flex; flex-direction:column; gap:8px; padding:14px; border-radius:16px;
      border:1px solid color-mix(in srgb, currentColor 14%, transparent); }
    .keep-h { font-weight:700; font-size:13.5px; }
    .keep-b { font-size:12.5px; color:var(--app-muted); line-height:1.5; }
    /* The field takes the whole width and the button sits under it. Side by
       side, the input was squeezed to whatever the button left over — and a
       phone number is long enough that it scrolled inside its own box while
       being typed, which is exactly when you want to see all of it. */
    .keep-row { display:flex; flex-direction:column; gap:9px; align-items:stretch; }
    .keep-row md-filled-button { width:100%; --md-filled-button-container-height:46px; }
    .keep-in { width:100%; box-sizing:border-box; font:inherit; font-size:15px; padding:11px 13px;
      border-radius:12px; color:inherit; background:transparent;
      border:1px solid color-mix(in srgb, currentColor 26%, transparent); }
    .keep-in:focus-visible { outline:2px solid var(--app-accent, currentColor); outline-offset:1px; }
    .keep-err { font-size:12.5px; color:var(--app-on-error-container, #ffb4ab); }
    .yours { display:flex; flex-direction:column; gap:2px; margin-top:2px; }
    .yours-h { font-size:10px; font-weight:600; text-transform:uppercase;
      letter-spacing:calc(.03em * var(--track, 1)); color:var(--app-muted); margin-bottom:4px; }
    .yours-row { display:flex; align-items:center; gap:8px; padding:9px 11px; border-radius:12px;
      font-size:13.5px; color:inherit; text-decoration:none;
      background:color-mix(in srgb, currentColor 5%, transparent); }
    .yours-row:hover { background:color-mix(in srgb, currentColor 10%, transparent); }
    /* A card issued but not yet claimed — centred, because it is the entire
       screen rather than a note beside a card. */
    .pending { display:flex; flex-direction:column; align-items:center; text-align:center; gap:10px;
      padding:28px 20px; border-radius:16px; margin-top:8px;
      background:color-mix(in srgb,var(--app-primary,#1b7a52) 10%, transparent);
      border:1px solid color-mix(in srgb,var(--app-primary,#1b7a52) 26%, transparent); }
    .pending-h { font-family:var(--font-brand,system-ui); font-weight:700; font-size:17px; }
    .pending-b { font-size:13px; color:var(--app-muted); line-height:1.55; max-width:34ch; }
    .pending md-filled-button { margin-top:6px; --md-filled-button-container-height:48px; }

    .mk { display:inline-flex; align-items:center; gap:7px; text-decoration:none; font-weight:600; font-size:13px; color:var(--app-text); border:1px solid var(--app-outline-strong); padding:11px 18px; border-radius:999px; }

    /* ── Desktop ────────────────────────────────────────────────────
       Phone layouts stretched to a desktop viewport give a 1400px-wide card and
       a single column of whitespace. Cap the measure, and let the directory use
       the width it has instead of stacking one card per row. */
    .col { display:flex; flex-direction:column; gap:15px; min-width:0; }

    @media (min-width:900px){
      section { max-width:1180px; margin:0 auto; width:100%; padding:26px 28px 34px; gap:18px; }
      /* Your card sits beside what you do with it, rather than pushing the QR
         a screen-height below. */
      .minegrid { display:grid; align-items:start;
        grid-template-columns:minmax(0,1.1fr) minmax(300px,.9fr); gap:26px 44px; }
      /* The directory is many cards, so it gains most from the extra width. */
      .directory .list { display:grid; grid-template-columns:repeat(auto-fill, minmax(330px,1fr));
        gap:20px; align-items:start; }
      .directory nfc-search { max-width:520px; }
    }
    @media (min-width:900px){
      .pp { max-width:900px; display:grid; align-content:center; gap:22px 48px;
        grid-template-columns:minmax(0,1.05fr) minmax(260px,.95fr);
        grid-template-areas:"brand brand" "card acts" "extra extra" "pitch pitch" "cta cta"; padding:32px 28px; }
      .pp card-extras { grid-area:extra; }
      .pp .brand { grid-area:brand; }
      .pp lead-card { grid-area:card; }
      .pp .ppacts { grid-area:acts; align-self:center; }
      .pp .cta { grid-area:cta; margin-top:14px; flex-direction:row; justify-content:center; gap:20px; }
    }
  `;

  connectedCallback() {
    super.connectedCallback();
    this._onHash = () => {
      const before = this.route.view;
      this.route = parseRoute();
      this.syncFromHash();
      // Opening or closing an overlay must not refetch the page behind it —
      // only an actual change of route needs data.
      if (before !== this.route.view) this.load();
    };
    window.addEventListener('hashchange', this._onHash);
    // Escape answers "keep it", like any other dismissable thing in the app.
    this._onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (this.deleting && !this.delBusy) { this.cancelDelete(); return; }
      if (this.noting) { void this.closeNote(); return; }
      if (this.dirTools || this.dirNotes) {
        this.dirTools = false; this.dirNotes = false;
      }
    };
    window.addEventListener('keydown', this._onKey);
    this.syncFromHash();
    this.load();
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('hashchange', this._onHash!);
    window.removeEventListener('keydown', this._onKey!);
    clearTimeout(this.doneTimer);
  }
  private _onHash?: () => void;
  private _onKey?: (e: KeyboardEvent) => void;

  /** Tapping a tag, scanning a QR or opening a shared link IS the act of
   *  receiving someone's card — so it lands in the collection by itself. Making
   *  people press a button afterwards asks them to confirm something they
   *  already did with a physical gesture.
   *
   *  Never collects one of your OWN cards: opening your own link to check it
   *  should not file you under your own contacts. */
  private async autoCollect(card: Card) {
    if (!card.publicId) return;
    if (this.mine.some((m) => m.publicId === card.publicId)) return;

    await saveCollected(toProjection(card));
    this.collected = await collectedCards();
    this.justCollected = true;
    try {
      this.channel = await recordCollect(card.publicId, this.my?.publicId ?? '');
    } catch {
      // Offline: it is collected locally regardless. Offering an invite we
      // could not check might message an existing user, so do not.
      this.channel = 'none';
    }
  }

  private async load() {
    // Once per launch, and never blocking anything: the notice is chrome, and a
    // page that waits on it to paint is worse than a page without it.
    if (!this.legal.configured) void fetchLegal().then((l) => { this.legal = l; });
    // A session only exists after an OTP claim. Roll it forward on every
    // launch so it never quietly expires (there is no logout in this app), then
    // reconcile with the account so the device shows what the person owns.
    if (signedIn()) {
      if (await keepAlive()) {
        try { await syncFromAccount(); } catch { /* offline — local data stands */ }
      }
    }
    if (this.route.view === 'profile') {
      // The person's own cards are needed first, to know whether this is one of
      // theirs and whether to offer them a card of their own.
      [this.mine, this.my] = await Promise.all([listMyCards<Card>(), myCard()]);
      this.profile = await publicCard(this.route.id);
      this.loaded = true;
      if (this.profile) await this.autoCollect(this.profile);
    } else {
      [this.my, this.mine, this.collected] = await Promise.all([myCard(), listMyCards<Card>(), collectedCards()]);
      this.loaded = true;
      // Show the stored copies immediately — they are the offline answer — then
      // catch them up with what their owners have published since. A collected
      // card that never updates makes a digital card no better than paper.
      void this.catchUpCollected();
    }
  }

  /** Re-read collected cards from the server, in the background. */
  private async catchUpCollected() {
    try {
      const { updated } = await refreshCollected(fetchProfiles);
      if (updated) this.collected = await collectedCards();
    } catch { /* offline — the stored copies stand */ }
  }

  render() {
    return html`
      ${this.route.view === 'profile' ? this.renderProfile() : this.renderApp()}
      ${this.claimingCardId && (this.need('claim'), this.claimReady)
        ? html`<claim-sheet .publicId=${this.claimingCardId} ?confirming=${this.claimIsNew}
            @claimed=${this.onClaimed}
            @close=${() => { this.claimingCardId = ''; this.claimIsNew = false; }}></claim-sheet>`
        : nothing}`;
  }

  private renderApp() {
    const filtered = this.tab === 'cards' && !!(this.query || this.industry);
    const open = this.dirTools;
    return html`
      <nfc-app-shell>
        <div class="page">
          ${this.tab === 'work' ? this.renderWork()
            : this.tab === 'cards' ? (this.dirNotes ? this.renderNotes() : this.renderDirectory())
            : this.renderMine()}
        </div>

        <!-- Just the field: no button, no sheet. Typing filters; that is the
             whole interaction. Everything else lives in the FAB menu. The same
             bar serves whichever list is on screen — cards, or notes. -->
        ${this.tab === 'cards' && !this.dirTools
          ? (this.dirNotes
              ? html`<nfc-search class="searchbar" .value=${this.noteQuery}
                  placeholder=${t('notes.search')}
                  @search=${(e: CustomEvent<string>) => { this.noteQuery = e.detail; }}></nfc-search>`
              : html`<nfc-search class="searchbar" .value=${this.query}
                  placeholder=${this.collected.length === 1
                    ? t('dir.searchOne')
                    : t('dir.search', { n: n(this.collected.length) })}
                  @search=${(e: CustomEvent<string>) => { this.query = e.detail; }}></nfc-search>`)
          : nothing}
        ${this.dirTools ? html`
          <button class="fab-scrim" aria-label=${t('dir.tools')}
            @click=${() => { this.dirTools = false; }}></button>
          ${this.renderFabMenu()}`
          : nothing}

        <!-- ONE control for the whole app: an M3 FAB menu — actions stack
             above the button over a scrim, and the FAB becomes the close.
             The dot warns that a filter is quietly shortening the card list. -->
        <button class=${classMap({ dirfab: true, filtered, open })}
          @click=${() => { this.dirTools = !this.dirTools; }}
          aria-expanded=${open ? 'true' : 'false'} aria-label=${t('dir.tools')}>
          ${icon(open ? 'close' : 'menu')}
        </button>
      </nfc-app-shell>`;
  }

  /** The M3 FAB menu: destinations and tools as icon+label pills stacked
   *  above the FAB, the current destination highlighted. Tapping one acts
   *  and closes; the scrim or the FAB (now ✕) closes without acting.
   *  No search here — on the directory the writable field sits beside the FAB.
   *  Collect and export live here too, so the field can be JUST a field.
   *  Notes on collected cards are their own interface, entered from here. */
  private renderFabMenu() {
    const item = (tab: Tab, ic: Parameters<typeof icon>[0], label: string) => {
      const on = this.tab === tab && !this.dirNotes;
      return html`
      <button class=${classMap({ fmi: true, on })}
        aria-current=${on ? 'page' : 'false'}
        @click=${() => {
          this.flushOpenEditor();
          this.goTab(tab); this.dirTools = false; this.dirNotes = false;
        }}>
        ${icon(ic)}<span>${label}</span>
      </button>`;
    };
    const notesOn = this.tab === 'cards' && this.dirNotes;
    return html`
      <nav class="fabmenu" aria-label=${t('dir.tools')}>
        <lang-picker></lang-picker>
        <button class="fmi" @click=${() => { this.dirTools = false; this.startCollect(); }}>
          ${icon('add')}<span>${t('dir.collect')}</span>
        </button>
        ${this.collected.length
          ? html`<button class="fmi" title=${t('dir.exportHint')}
              @click=${() => { this.dirTools = false; this.exportContacts(); }}>
              ${icon('save')}<span>${t('dir.export')}</span>
            </button>`
          : nothing}
        ${item('home', 'badge', t('nav.home'))}
        ${item('work', 'group', t('nav.work'))}
        ${item('cards', 'collections_bookmark', t('nav.cards'))}
        <button class=${classMap({ fmi: true, on: notesOn })}
          aria-current=${notesOn ? 'page' : 'false'}
          @click=${() => {
            this.flushOpenEditor();
            this.goTab('cards'); this.dirNotes = true; this.dirTools = false;
            void this.loadNotes();
          }}>
          ${icon('edit')}<span>${t('dir.notes')}</span>
        </button>
      </nav>`;
  }

  /**
   * A card was deleted on the server, so forget it here too.
   *
   * The order matters: the server has already stopped resolving it, so keeping
   * the local copy would leave a card in the deck whose link is dead — worse
   * than not having it, because it looks like it still works.
   */
  private onCardDeleted = async (e: CustomEvent<string>) => {
    const gone = this.mine.find((c) => c.publicId === e.detail);
    if (gone?.id) await deleteMyCard(gone.id);
    this.closeOverlay();
    [this.my, this.mine] = await Promise.all([myCard(), listMyCards<Card>()]);
  };

  private onCardSaved = async () => {
    this.closeOverlay();
    [this.my, this.mine] = await Promise.all([myCard(), listMyCards<Card>()]);

    // Creating a card IS signing up. The card carries a phone or an email, so
    // the moment it is published we can prove it belongs to whoever made it —
    // a code goes out (by SMS where possible, which the browser fills in
    // itself) and they tap Done. No signup form, no password, no login screen,
    // and their card follows them to any device from then on.
    //
    // Only for a card that was actually published (a local-only one, saved
    // offline, has nothing to claim yet) and only when not already signed in.
    if (!signedIn() && this.my?.publicId && (this.my.phone || this.my.email)) {
      this.claimingCardId = this.my.publicId;
      this.claimIsNew = true;
    }
  };

  /** Your own card(s): the switcher, the active card, and what you do with it. */
  private renderMine() {
    // The editor takes over the section: creating or editing a card is a task,
    // not something to do beside a directory.
    if (this.editorMode || (this.loaded && !this.my)) {
      this.need('editor');
      // The editor IS the screen here, so it is the one place that reports
      // loading rather than upgrading quietly under the user.
      if (!this.editorReady) {
        return html`<section class="mine"><div class="empty">${t('mine.loading')}</div></section>`;
      }
      return html`<section class="mine editing">
        <creator-flow .card=${this.editorMode === 'edit' ? this.my : null}
          .existing=${this.mine}
          @open-existing=${this.onOpenExisting}
          @deleted=${this.onCardDeleted}
          @saved=${this.onCardSaved}></creator-flow>
      </section>`;
    }
    const c = this.my;
    if (!c) return html`<section class="mine"><div class="empty">${t('mine.loading')}</div></section>`;

    return html`
      <section class="mine">
        <div class="minegrid">
          <div class="col">
            ${this.mine.length > 1
              ? this.renderDeck(c)
              : html`<div class=${classMap({ targeted: this.deleting === c.publicId })}>
                  ${this.renderCard(c)}
                </div>`}

            <!-- Directly under the card, because they act ON it: edit THIS
                 card, or start another. Above the card they read as page
                 chrome and it is never clear which card "Edit" means.

                 Nothing is inserted ABOVE this row when Delete is pressed. It
                 used to grow a line and a caption there, which pushed the row
                 88px down in the frame after the tap and left the line itself
                 under the finger — so a second tap, or a slightly late one,
                 hit something that had not been there when it was aimed at. -->
            <div class=${classMap({ cardacts: true, targeting: !!this.deleting })}>
              <md-filled-button class="primary" ?disabled=${!!this.deleting} @click=${this.editActive}>
                ${icon('edit', 'icon')}${t('mine.edit')}
              </md-filled-button>
              <div class="minoracts">
                <md-text-button ?disabled=${!!this.deleting} @click=${this.newCard}>
                  ${icon('add', 'icon')}${t('mine.create')}
                </md-text-button>
                <md-text-button class="del" aria-expanded=${this.deleting ? 'true' : 'false'}
                  @click=${this.startDelete}>
                  ${icon('close', 'icon')}${t('mine.delete')}
                </md-text-button>
              </div>
            </div>
            ${this.renderDeleteConfirm(c)}
            ${this.delDone
              ? html`<p class="deldone" role="status">${icon('check')}${t('mine.deleted', { label: this.delDone })}</p>`
              : nothing}

            <!-- How often this card has been opened, WITH the card. It used to
                 sit under the share panel in the other column, which on a phone
                 is a screen-height below the fold: the one figure that tells
                 someone the thing is working was where nobody scrolled.

                 Below the actions rather than above them, because the rule that
                 nothing comes between the card and what you do with it still
                 holds — and it must not move the delete button, so it renders
                 in the same place whether or not there are taps to report.

                 Either credential reads it: the edit token belongs to the
                 device that published the card, the session to any device the
                 owner has signed in on. -->
            ${c.publicId && (c.editToken || signedIn()) && (this.need('stats'), this.statsReady)
              ? html`<card-stats class="taps" .publicId=${c.publicId} .editToken=${c.editToken ?? ''}></card-stats>`
              : nothing}
          </div>
          <div class="col">
            ${(this.need('share'), this.shareReady)
              ? html`<share-card .url=${shareUrl(c.id ?? '')} .publicId=${c.publicId ?? ''} .editToken=${c.editToken ?? ''}></share-card>`
              : nothing}
          </div>
        </div>
      </section>`;
  }


  /** Switching destination is a navigation, so Back returns to the last one. */
  private goTab(tab: Tab) {
    if (this.tab === tab) { this.shell?.scrollToTop(); return; }
    const hash = TAB_HASH[tab];
    if (hash) location.hash = hash;
    else location.replace(location.pathname + location.search);
    this.syncFromHash();
  }

  private renderCard(c: Card) {
    return html`
      <lead-card .name=${c.name} .title=${c.title} .department=${c.department ?? ''} .company=${c.company} .monogram=${c.monogram}
        .phone=${c.phone} .email=${c.email} .website=${c.website} .address=${c.address}
        .theme=${c.theme} .styleVars=${c.styleVars ?? ''} .tagline=${c.tagline}
        .url=${shareUrl(c.id ?? '')} .photo=${c.photo ?? ''} .backImage=${c.backImage ?? ''}
        .orgName=${c.org?.name ?? ''} ?orgVerified=${c.org?.verified === true} .eventName=${c.org?.event ?? ''}></lead-card>`;
  }

  /**
   * More than one card of your own, shown as a deck you swipe.
   *
   * A row of chips under a single card said you had others only if you read it.
   * Letting the next card show at the edge says it without words — the thing
   * you are looking at is visibly one of several, and the gesture for getting
   * to the next one is the one people already use for cards.
   *
   * Scroll-snap does the work, so it is a native horizontal scroll: it flicks
   * with momentum, works with a trackpad, and needs no drag handling.
   */
  private renderDeck(active: Card) {
    const at = Math.max(0, this.mine.findIndex((m) => m.publicId === active.publicId));
    return html`
      <div class=${classMap({ deck: true, targeting: !!this.deleting })}
        role="group" aria-label=${t('mine.deck')} @scroll=${this.onDeckScroll}>
        ${this.mine.map((card) => html`
          <div class=${classMap({ slide: true, targeted: !!this.deleting && card.publicId === this.deleting })}
            aria-current=${card.publicId === active.publicId ? 'true' : 'false'}>
            ${this.renderCard(card)}
          </div>`)}
      </div>
      <div class="deckbar">
        <div class="dots">
          ${this.mine.map((card, i) => html`
            <button class=${classMap({ dot: true, on: i === at })}
              aria-label=${t('mine.goToCard', { name: cardLabel(card) })}
              @click=${() => this.goToCard(i)}></button>`)}
        </div>
        <span class="deckcount">${t('mine.nOfTotal', { n: n(at + 1), total: n(this.mine.length) })}</span>
      </div>`;
  }

  /** Which card is centred, once the flick has settled. */
  private onDeckScroll = (e: Event) => {
    const deck = e.currentTarget as HTMLElement;
    clearTimeout(this.deckTimer);
    this.deckTimer = setTimeout(() => {
      const slide = deck.querySelector('.slide') as HTMLElement | null;
      if (!slide) return;
      const i = Math.round(deck.scrollLeft / slide.offsetWidth);
      const card = this.mine[i];
      // Only when it actually changed: switching rewrites the active card in
      // storage and reloads the panel beside it.
      if (card?.publicId && card.publicId !== this.my?.publicId) {
        this.deckFromScroll = true;
        void this.switchCard(card.publicId);
      }
    }, 120);
  };
  private deckTimer?: ReturnType<typeof setTimeout>;

  private goToCard(i: number) {
    const deck = this.renderRoot.querySelector('.deck') as HTMLElement | null;
    const slide = deck?.querySelector('.slide') as HTMLElement | null;
    if (deck && slide) deck.scrollTo({ left: i * slide.offsetWidth, behavior: 'smooth' });
  }

  /**
   * The company section: employee cards a business issues and runs.
   *
   * Only shown to someone signed in, because it is meaningless otherwise — an
   * organisation needs an identity to administer it. It sits last on the page:
   * most people never open it, and the two sections above are what they came
   * for.
   */
  private renderWork() {
    return html`
      <section class="work">
        <!-- Shown to everyone, including before sign-in. Hiding it entirely is
             what made it look missing: the top bar now points here, so landing
             on nothing would be worse than landing on the reason. -->
        ${signedIn()
          ? ((this.need('work'), this.workReady) ? html`<org-panel></org-panel>` : nothing)
          : html`<div class="orggate">
              <p>${t('org.pitch')}</p>
              <p class="hint">${t('org.needIdentity')}</p>
              <md-filled-button @click=${this.goToWork}>
                ${icon('badge', 'icon')}${t(this.my?.publicId ? 'org.claimCardFirst' : 'org.makeCardFirst')}
              </md-filled-button>
            </div>`}
      </section>`;
  }

  /**
   * Start the company flow — from wherever the person currently stands.
   *
   * An organisation is administered by a person, so the server needs a proven
   * identity before it will create one. Signed out, this used to scroll to a
   * paragraph explaining that, which is a dead end: the app's primary call to
   * action landed on a reason it could not be done. It now takes the first step
   * itself — make a card, or confirm the one already here — and comes back.
   */
  /** Take possession of a card issued to you. Opens the same sheet the rest of
   *  the app uses, so proving a work address stays one flow with one set of
   *  edge cases rather than a second one written for this screen. */
  private startClaim(publicId: string) {
    if (!publicId) return;
    this.claimingCardId = publicId;
    this.claimIsNew = false;
  }

  private goToWork = () => {
    if (signedIn()) { this.scrollToWork(); return; }
    this.headingForWork = true;
    if (this.my?.publicId) {
      // A card is already here, it just has not been proved. Confirm that.
      this.claimingCardId = this.my.publicId;
    } else {
      this.newCard();
    }
  };

  private scrollToWork() {
    void this.updateComplete.then(() => {
      this.renderRoot.querySelector('section.work')
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  /** The collected directory: search always, industry sort once it is big
   *  enough to need one. */
  private renderDirectory() {
    const total = this.collected.length;
    const industries = industriesIn(this.collected);
    // Below this a directory is scannable by eye, and a filter row would be
    // chrome that never earns its space.
    const showSort = total > SORT_THRESHOLD && industries.length > 1;

    let items = this.collected.filter((c) => matches(c as unknown as Record<string, unknown>, this.query));
    if (showSort && this.industry) items = items.filter((c) => industryOf(c) === this.industry);
    if (showSort) items = [...items].sort(byIndustryThenName);

    return html`
      <section class="directory">
        ${items.length
          ? html`<div class="list">${items.map((c) => html`
              <lead-card .name=${c.name} .title=${c.title} .department=${c.department ?? ''} .company=${c.company} .monogram=${c.monogram}
                .phone=${c.phone} .email=${c.email} .website=${c.website} .address=${c.address}
                .theme=${c.theme} .styleVars=${c.styleVars ?? ''} .tagline=${c.tagline}
                .url=${shareUrl(c.id ?? '')} .photo=${c.photo ?? ''} .backImage=${c.backImage ?? ''}
                .orgName=${c.org?.name ?? ''} ?orgVerified=${c.org?.verified === true} .eventName=${c.org?.event ?? ''}></lead-card>`)}</div>`
          : html`<div class="empty">${t(total ? 'dir.noMatch' : 'dir.empty')}</div>`}

        ${this.collecting && (this.need('collect'), this.collectReady)
          ? html`<div class="collect-overlay" @click=${this.onOverlayClick}>
              <collect-flow @collected=${this.onCollected} @close=${() => this.closeOverlay()}></collect-flow>
            </div>`
          : nothing}
      </section>`;
  }

  /** Whether the bottom navigation bar is open. */
  @state() private dirTools = false;
  /** Whether the notes interface replaces the card list (directory only). */
  @state() private dirNotes = false;

  /** Every standalone note, newest-touched first. */
  @state() private notes: Note[] = [];
  /** Which note is open for editing: a note's id, 'new' for a fresh one,
   *  or '' for none. */
  @state() private noting = '';
  /** What the bottom bar is filtering the notes list to. */
  @state() private noteQuery = '';
  /** Flash "Copied" on the share button where Web Share does not exist. */
  @state() private noteShared = false;
  /** The real id behind the open editor: '' until a new note's first autosave
   *  creates one. Kept OFF `noting` so the editor never jumps position when a
   *  draft quietly becomes a stored note mid-typing. */
  private draftId = '';
  private noteSaveTimer = 0;

  private async loadNotes() {
    try { this.notes = await listNotes(); } catch { this.notes = []; }
  }

  /** Notes, their own interface (FAB menu → Notes): a plain notes app.
   *  Write a note, read it back, change it, delete it — nothing here knows
   *  or cares about cards. Everything stays on this device. */
  private renderNotes() {
    const q = this.noteQuery.trim().toLowerCase();
    const shown = q ? this.notes.filter((x) => x.text.toLowerCase().includes(q)) : this.notes;
    return html`
      <section class="directory notes">
        ${this.noting === 'new'
          ? this.renderNoteEditor(null)
          : html`<button class="notebtn takenote" @click=${() => this.openNote('new')}>
              ${icon('add')}<span>${t('notes.take')}</span>
            </button>`}
        ${shown.length
          ? shown.map((note) => html`
              <div class="noteent">
                ${this.noting === note.id
                  ? this.renderNoteEditor(note)
                  : html`<button class="noteitem" @click=${() => this.openNote(note.id)}>
                      <span class="notetext">${note.text}</span>
                      <time class="notedate">${relativeTime(note.updatedAt)}</time>
                    </button>`}
              </div>`)
          : this.noting === 'new' ? nothing
            : html`<div class="empty">${t(q ? 'notes.noMatch' : 'notes.empty')}</div>`}
      </section>`;
  }

  /** One open note. There is no cancel, because there is nothing to cancel:
   *  the draft saves itself as it is typed, so closing is the only verb left —
   *  and a note emptied out and closed deletes itself. */
  private renderNoteEditor(note: Note | null) {
    return html`
      <div class="noteedit">
        <textarea class="noteinput" rows="3" .value=${note?.text ?? ''}
          placeholder=${t('notes.ph')} aria-label=${t('dir.notes')}
          @input=${this.onNoteInput}
          @keydown=${(e: KeyboardEvent) => {
            if (e.key === 'Escape') { e.stopPropagation(); void this.closeNote(); }
          }}></textarea>
        <div class="noteacts">
          ${note || this.draftId
            ? html`<md-text-button class="notedel" @click=${() => this.removeNote()}>
                ${t('notes.delete')}
              </md-text-button>`
            : nothing}
          <md-text-button class="noteshare" @click=${() => this.shareNote()}>
            ${icon('ios_share', 'icon')}${t(this.noteShared ? 'notes.copied' : 'notes.share')}
          </md-text-button>
          <md-text-button class="notesave" @click=${() => this.closeNote()}>${t('dir.noteSave')}</md-text-button>
        </div>
      </div>`;
  }

  private openNote(id: string) {
    // A different note may be open with unflushed keystrokes; hand them to
    // THEIR note before this one exists to receive them by mistake.
    this.flushOpenEditor();
    this.noting = id;
    this.draftId = id === 'new' ? '' : id;
    void this.updateComplete.then(() => {
      (this.renderRoot.querySelector('.noteinput') as HTMLTextAreaElement | null)?.focus();
    });
  }

  /** Abandon the open editor safely: read its text NOW — synchronously, before
   *  any re-render can tear the textarea down — cancel the pending debounce,
   *  and save what was there under its own draft id. Without this, switching
   *  views or notes mid-edit left `draftId` aimed at the old note while the
   *  editor came back empty, and the next keystrokes overwrote it. */
  private flushOpenEditor() {
    if (!this.noting) return;
    clearTimeout(this.noteSaveTimer);
    const box = this.renderRoot.querySelector('.noteinput') as HTMLTextAreaElement | null;
    const id = this.draftId || undefined;
    this.noting = '';
    this.draftId = '';
    if (box) void saveNote(box.value, id).then(() => this.loadNotes());
  }

  /** Never lose a word: the draft writes itself through to storage as it is
   *  typed, so an interruption mid-note — a call, a tap, a dead battery —
   *  costs at most the debounce window. */
  private onNoteInput = (e: Event) => {
    const text = (e.target as HTMLTextAreaElement).value;
    clearTimeout(this.noteSaveTimer);
    this.noteSaveTimer = window.setTimeout(() => { void this.flushDraft(text); }, 600);
  };

  private async flushDraft(text: string) {
    const saved = await saveNote(text, this.draftId || undefined);
    this.draftId = saved?.id ?? '';
  }

  /** Close the editor: one last flush, then show the list. */
  private async closeNote() {
    clearTimeout(this.noteSaveTimer);
    const box = this.renderRoot.querySelector('.noteinput') as HTMLTextAreaElement | null;
    if (box) await this.flushDraft(box.value);
    this.noting = '';
    this.draftId = '';
    await this.loadNotes();
  }

  private async removeNote() {
    clearTimeout(this.noteSaveTimer);
    if (this.draftId) await deleteNote(this.draftId);
    this.noting = '';
    this.draftId = '';
    await this.loadNotes();
  }

  /** Hand the note somewhere else — a note is often a thing promised to
   *  someone. Web Share where the platform has it, the clipboard where not. */
  private async shareNote() {
    const text = (this.renderRoot.querySelector('.noteinput') as HTMLTextAreaElement | null)?.value.trim();
    if (!text) return;
    if (navigator.share) {
      try { await navigator.share({ text }); } catch { /* dismissed the sheet */ }
      return;
    }
    try {
      await navigator.clipboard?.writeText(text);
      this.noteShared = true;
      setTimeout(() => { this.noteShared = false; }, 1600);
    } catch { /* no clipboard either — the text is still on screen */ }
  }

  /**
   * Take the collection somewhere else.
   *
   * Exports what is ON SCREEN, not everything held: somebody who has searched or
   * filtered down to the twelve people they met this week means those twelve, and
   * silently handing back four hundred would be the wrong answer to the gesture
   * they just made.
   */
  private exportContacts = () => {
    const shown = this.collected
      .filter((c) => matches(c as unknown as Record<string, unknown>, this.query))
      .filter((c) => !this.industry || industryOf(c) === this.industry);
    downloadCSV(shown as unknown as CollectedCard[]);
  };

  /** Read the overlay state out of the URL. The hash is the source of truth,
   *  so a Back press and a tap on Close go through exactly the same path. */
  private syncFromHash() {
    const { editor, collecting } = overlayFromHash(location.hash);
    this.editorMode = editor;
    this.collecting = collecting;
    this.tab = tabFromHash(location.hash);
    if (!this.editorMode && !this.collecting) this.pushedOverlay = false;
  }

  /**
   * Reset the scroll whenever the editor comes or goes.
   *
   * It replaces the whole top of the page, so the old offset means nothing
   * against the new content: without this, leaving the editor left you looking
   * at the QR code with your card scrolled off the top. Keyed on what is
   * actually rendered rather than on the hash, because the editor also opens
   * with no hash at all — for somebody who has no card yet, which is precisely
   * the person who then saves their first one.
   *
   * The scrolling element is inside the shell's shadow root, beyond the reach
   * of the browser's own history scroll restoration, so it is asked directly.
   */
  protected updated() {
    const open = this.editorMode !== null || (this.loaded && !this.my);
    if (open !== this.editorWasOpen) {
      this.editorWasOpen = open;
      this.shell?.scrollToTop();
    }
    this.syncDeck();
    // The confirmation is aimed at ONE card, so it closes the moment a different
    // card becomes the active one — swiping the deck with it open would
    // otherwise leave "Delete it" pointed at whatever arrived under it.
    if (this.deleting && this.my?.publicId !== this.deleting) {
      this.deleting = '';
      this.delError = '';
    }
    const nav = this.renderRoot.querySelector('.nav') as HTMLElement | null;
    document.documentElement.style.setProperty('--nav-h',
      nav ? `${Math.ceil(nav.getBoundingClientRect().height)}px` : '0px');
  }
  private editorWasOpen = false;

  /**
   * Keep the deck showing the card that is actually active.
   *
   * The active card also changes from outside the deck — saving a new one makes
   * it active — and without this the counter read "3 of 3" while the track was
   * still parked on the first card. Skipped when the change came FROM a swipe,
   * which would otherwise fight the finger that caused it.
   */
  private syncDeck() {
    const id = this.my?.publicId;
    if (!id || this.mine.length < 2) { this.deckAt = ''; this.deckTries = 0; return; }
    if (this.deckFromScroll) { this.deckFromScroll = false; this.deckAt = id; return; }
    if (this.deckAt === id) return;

    // Jump rather than glide on the first paint; a card sliding in on load
    // looks like something is loading rather than like an answer.
    if (this.showInDeck(id, this.deckAt ? 'smooth' : 'auto')) {
      this.deckAt = id;
      this.deckTries = 0;
      return;
    }
    // The deck was not ready to scroll. Try again on the next frames rather than
    // recording a position it never took — that was the actual reason the
    // counter could read "2 of 2" over a track sitting on the first card: the
    // scroll ran once, silently did nothing, and was never attempted again.
    // Bounded, because a deck whose cards all fit (desktop, two side by side)
    // has nothing to scroll and never will.
    if (this.deckTries++ < 5) requestAnimationFrame(() => this.syncDeck());
  }
  private deckTries = 0;

  /**
   * Park the deck on one card.
   *
   * Scrolls the slide itself rather than multiplying an index by a slide width:
   * the slides are separated by a gap and the track is padded, so the arithmetic
   * landed short of the card it named and the counter could read "2 of 3" over a
   * track still showing the first. Reports whether it found the slide, so a
   * caller can leave its bookkeeping alone when the deck has not rendered yet.
   */
  private showInDeck(id: string, behavior: ScrollBehavior = 'smooth'): boolean {
    const deck = this.renderRoot.querySelector('.deck') as HTMLElement | null;
    const i = this.mine.findIndex((m) => m.publicId === id);
    const slide = i < 0 ? null : (deck?.querySelectorAll('.slide')[i] as HTMLElement | undefined);
    if (!deck || !slide) return false;
    // On the first paint the deck can report no overflow at all — the slides are
    // laid out (the second one's offsetLeft is already past the edge) but the
    // scroll area is not final — and scrolling then does exactly nothing.
    // Measured: scrollWidth 390 against a clientWidth of 390 with slide 2 at
    // offsetLeft 343. Report that as "not yet" so the caller retries instead of
    // recording a position the deck never took.
    if (deck.scrollWidth <= deck.clientWidth + 1) return false;
    slide.scrollIntoView({ inline: 'start', block: 'nearest', behavior });
    return true;
  }
  /** Public id the deck is currently parked on. */
  private deckAt = '';
  private deckFromScroll = false;

  private get shell() {
    return this.renderRoot.querySelector('nfc-app-shell');
  }

  /** Opening pushes a history entry — that push is the whole point: it is what
   *  gives Back something to return to. */
  private openOverlay(hash: string) {
    this.pushedOverlay = true;
    location.hash = hash;
  }

  /** Closing from inside the app (Save, Done, the backdrop). Going BACK rather
   *  than pushing a hash-less entry matters: pushing would leave the editor one
   *  Back press away, so Back would re-open what was just closed. */
  private closeOverlay() {
    if (this.pushedOverlay) { this.pushedOverlay = false; history.back(); return; }
    // Deep-linked straight into the overlay, so there is nothing behind it to
    // go back to; drop the hash without adding an entry.
    history.replaceState(null, '', location.pathname + location.search);
    this.syncFromHash();
  }

  private switchCard = async (id: string) => {
    if (!id) return;
    await setActiveCard(id);
    this.my = await myCard();
  };

  /** "Open that card instead" — switch to the card being duplicated and edit
   *  it, which is what the person meant to do in the first place. */
  private onOpenExisting = async (e: CustomEvent<string>) => {
    await this.switchCard(e.detail);
    location.replace(location.pathname + location.search + EDIT_HASH);
    this.syncFromHash();
  };

  /** Which card the delete confirmation is aimed at, or '' when closed. */
  @state() private deleting = '';
  /** The name of that card, held separately so it does not change to the next
   *  card's name half-way through the closing animation. */
  @state() private delLabel = '';
  /** Why the last attempt did not go through, shown in the confirmation. */
  @state() private delError = '';
  /** True while the request is in flight, so it cannot be fired twice. */
  @state() private delBusy = false;
  /** The card just deleted, acknowledged briefly. Without it a card simply
   *  vanished and the only evidence anything happened was its absence. */
  @state() private delDone = '';
  private doneTimer = 0;

  private startDelete = () => {
    const id = this.my?.publicId ?? '';
    if (this.deleting === id) { this.cancelDelete(); return; }
    this.deleting = id;
    this.delLabel = this.my ? cardLabel(this.my) : '';
    this.delError = '';
    this.delDone = '';
    void this.updateComplete.then(() => {
      // Bring the card being answered about onto the screen. A ring around a
      // card sitting off the edge of the deck is not an answer to "which one" —
      // and the deck is exactly where somebody has several to tell apart.
      this.showInDeck(this.deleting);
      // Focus the question, not the destructive button: a stray Enter should
      // not be able to finish this, and a screen reader should hear what is
      // being asked before it hears the way to agree.
      (this.renderRoot.querySelector('.delbox') as HTMLElement | null)?.focus();
      this.renderRoot.querySelector('.delslot')?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    });
  };

  private cancelDelete = () => {
    this.deleting = '';
    this.delError = '';
  };

  /**
   * Confirming a deletion, under the row the question was asked from.
   *
   * In the page rather than a browser dialog, and it names the card, because
   * the deck holds several and the button is one of three in a row: "are you
   * sure" is not answerable without knowing which card and what happens to it.
   *
   * Always rendered, collapsed to nothing when closed, so opening and closing
   * are a height that animates rather than content that appears from nowhere —
   * and so the buttons above it never move. `inert` keeps the collapsed copy
   * out of the tab order and away from a screen reader.
   */
  private renderDeleteConfirm(c: Card) {
    const open = !!this.deleting;
    return html`
      <div class=${classMap({ delslot: true, open })}>
        <div class="delinner">
          <div class="delbox" role="alertdialog" tabindex="-1" aria-modal="false"
            aria-label=${t('edit.delTitleOf', { label: this.delLabel })} ?inert=${!open}>
            <div class="del-h">${icon('close')}${t('edit.delTitleOf', { label: this.delLabel })}</div>
            <p class="del-b">${t('edit.delBody')}</p>
            <p class="del-b strong">${t('edit.delForever')}</p>
            ${this.delError
              ? html`<p class="del-err" role="alert">${icon('close')}<span>${this.delError}</span></p>`
              : nothing}
            <div class="del-a">
              <md-text-button @click=${this.cancelDelete}>${t('edit.delCancel')}</md-text-button>
              <md-filled-button ?disabled=${this.delBusy} @click=${() => this.confirmDelete(c)}>
                ${this.delBusy ? t('edit.delGoing') : t('edit.delGo')}
              </md-filled-button>
            </div>
          </div>
        </div>
      </div>`;
  }

  /**
   * Take the targeted card out of service.
   *
   * Deliberately does NOT require an edit token. That token is issued once, to
   * the device that published, so on a device the card was SYNCED to there is
   * none — and requiring one made the button do nothing at all there: the
   * confirmation opened and pressing Delete returned silently. The server
   * accepts the owner's session just as it accepts the token, so both go and it
   * decides.
   *
   * A 404 is the server's answer both to "no card" and to "you have not proved
   * this is yours", so it is resolved rather than reported: `fetchProfiles`
   * (which records NO tap, unlike the single-id route) says whether the card is
   * live. Live means this device cannot prove ownership — say so, and leave the
   * card alone. Not live means there is nothing to take out of service, and the
   * honest thing is to forget it here so the deck matches reality.
   */
  private confirmDelete = async (c: Card) => {
    if (!c.publicId || this.delBusy) return;
    this.delBusy = true;
    this.delError = '';
    try {
      await deleteCard(c.publicId, c.editToken);
      await this.forgetCard(c);
    } catch (err) {
      const status = err instanceof ApiError ? err.status : -1;
      if (status === 404 && !(await fetchProfiles([c.publicId])).length) {
        await this.forgetCard(c);
      } else {
        this.delError = status === 0 || status === -1
          ? t('edit.delOffline')
          : status === 404 ? t('edit.delNeedProof') : t('edit.delFailed');
      }
    } finally {
      this.delBusy = false;
    }
  };

  /** Drop a card from this device and land on whatever is left. */
  private async forgetCard(c: Card) {
    const label = this.delLabel || cardLabel(c);
    this.deleting = '';
    this.delError = '';
    if (c.id) await deleteMyCard(c.id);
    [this.my, this.mine] = await Promise.all([myCard(), listMyCards<Card>()]);
    // Say it happened, then get out of the way. A card that simply disappears
    // leaves its absence as the only evidence, which is a poor answer when the
    // deck has five that look alike.
    this.delDone = label;
    clearTimeout(this.doneTimer);
    this.doneTimer = window.setTimeout(() => { this.delDone = ''; }, 5000);
  }

  private editActive = () => this.openOverlay(EDIT_HASH);
  private newCard = () => this.openOverlay(NEW_HASH);
  private startCollect = () => this.openOverlay(COLLECT_HASH);

  private onOverlayClick = (e: Event) => {
    if (e.target === e.currentTarget) this.closeOverlay(); // click the backdrop to dismiss
  };

  private onCollected = async () => {
    this.collected = await collectedCards(); // refresh the list behind the sheet
  };

  private renderProfile() {
    const c = this.profile;
    if (!c) return html`<div class="pp"><div class="empty">${t('recv.notFound')}</div></div>`;
    // A card the company issued and its holder has not claimed. Everything on
    // it is blank by design, so rendering the normal card would show an empty
    // one. The person who belongs here reached this from the link mailed to
    // their work address, and claiming is the whole point of their visit.
    if (c.pending) {
      return html`
        <div class="pp">
          <div class="brand"><span class="dot"></span>Business Card<em>${t('recv.shared')}</em></div>
          <div class="pending">
            <div class="pending-h">${t('recv.pendingHead')}</div>
            <div class="pending-b">${t('recv.pendingBody')}</div>
            <md-filled-button @click=${() => this.startClaim(c.publicId ?? '')}>
              ${icon('how_to_reg', 'icon')}${t('recv.pendingCta')}
            </md-filled-button>
          </div>
        </div>`;
    }
    return html`
      <div class="pp">
        <div class="brand"><span class="dot"></span>Business Card<em>${t('recv.shared')}</em></div>
        <lead-card .name=${c.name} .title=${c.title} .department=${c.department ?? ''} .company=${c.company} .monogram=${c.monogram}
          .phone=${c.phone} .email=${c.email} .website=${c.website} .address=${c.address}
          .theme=${c.theme} .styleVars=${c.styleVars ?? ''} .tagline=${c.tagline}
          .url=${shareUrl(c.id ?? '')} .photo=${c.photo ?? ''} .backImage=${c.backImage ?? ''}
          .extras=${c.extras ?? null} .extraPhones=${c.extraPhones ?? []} .extraEmails=${c.extraEmails ?? []}
          .socials=${c.socials ?? null}
            .orgName=${c.org?.name ?? ''} ?orgVerified=${c.org?.verified === true} .eventName=${c.org?.event ?? ''}></lead-card>
        <!-- The rows on the card ARE the actions — every one taps through to
             the dialer / mail / maps / browser. One line says so; the Call and
             Email buttons that used to repeat two of those rows are gone. -->
        <div class="tap-hint">${icon('call')}${t('recv.tapHint')}</div>
        <!-- The disclosure the check-in fence requires (spec.event-mode §4):
             the person CARRYING the badge can read what it does, on the badge.
             Renders only on event cards — an ordinary card records nothing. -->
        ${c.org?.event
          ? html`<div class="checkin-note">${icon('how_to_reg')}${t('recv.checkinNote', { event: c.org.event })}</div>`
          : nothing}
        <!-- Already done, not offered: the tap was the action.
             Only for somebody who is signed in: for everybody else this line is
             replaced by the field that MAKES it true, below the actions. Saying
             "saved to your cards" to a first-time visitor overstates it — it is
             saved to this browser, and clearing site data loses it. -->
        ${this.justCollected && signedIn()
          ? html`<div class="saved">${icon('check')}${t('recv.saved')}${this.collected.length > 1
              ? html` · <a href="/">${t('recv.seeAll', { n: n(this.collected.length) })}</a>` : nothing}</div>`
          : nothing}
        <div class="ppacts">
          <!-- The place people actually look somebody up is their own phone's
               contacts, not this app — so this is the primary action on a card
               somebody has just been handed. -->
          <md-filled-button class="tocontacts" @click=${() => this.saveContact(c)}>
            ${icon(this.savedContact ? 'check' : 'person_add', 'icon')}
            ${t(this.savedContact ? 'recv.savedContact' : 'recv.saveContact')}
          </md-filled-button>
          <!-- Directly under "save to phone", because the two are the same
               thought: you have just been handed a card, here is how you keep it
               and here is how you hand one back. It used to sit at the bottom of
               the page in a pitch block, below everything. -->
          ${this.loaded && !signedIn()
            ? html`<md-outlined-button class="mkcard" @click=${this.startMyCard}>
                ${icon('badge', 'icon')}${t('recv.pitchCta')}
              </md-outlined-button>`
            : nothing}
          ${this.renderKeepCards()}

          <!-- One tap. Opens the recipient's own messaging app pre-filled, so
               the invite is person-to-person and the platform never messages a
               stranger on someone's behalf. -->
          ${this.justCollected && this.channel === 'external'
            ? html`<md-text-button class="invite" @click=${() => this.invite(c)}>
                ${icon('ios_share', 'icon')}${t('recv.invite', { name: c.name?.split(' ')[0] || '' })}
              </md-text-button>`
            : nothing}
        </div>
        <!-- Behind the front: links and promo. Rendered below the card, so no
             amount of extra content can change the card itself. -->
        ${c.backImage && (c.extras || c.extraPhones?.length || c.extraEmails?.length
            || Object.keys(c.socials ?? {}).some((k) => k !== 'website'))
          ? html`<card-extras .extras=${c.extras} .socials=${c.socials ?? null}
              .phones=${c.extraPhones} .emails=${c.extraEmails}></card-extras>`
          : nothing}
        <div class="cta">
          <div>${t('recv.noApp')}</div>
          ${c.publicId && c.claimed === false
            ? html`<md-text-button @click=${() => { this.claimingCardId = c.publicId ?? ''; }}>
                ${icon('how_to_reg', 'icon')}${t('recv.mine')}
              </md-text-button>`
            : nothing}
        </div>
        <!-- Deliberately quiet, but on the card itself: a tapped card is often
             the only page a person ever sees, so the way to object to one
             cannot live in an app they never installed. The server half has
             existed since T-701; this is the button it was waiting for. -->
        ${c.publicId
          ? html`<button class="report-link" @click=${() => { this.need('report'); this.reporting = c.publicId ?? ''; }}>
              ${icon('flag')}${t('recv.report')}
            </button>`
          : nothing}
        ${this.reporting && this.reportReady
          ? html`<div class="collect-overlay" @click=${(e: Event) => { if (e.target === e.currentTarget) this.reporting = ''; }}>
              <report-sheet .publicId=${this.reporting} @close=${() => { this.reporting = ''; }}></report-sheet>
            </div>`
          : nothing}
        ${this.renderLegal()}
      </div>`;
  }

  /**
   * Who operates this service, and how to reach them about the data it holds.
   *
   * Rendered on the PUBLIC card page as well as inside the app, because a
   * tapped card is frequently the only page a person ever sees — a notice
   * reachable only from an app they never installed is not reachable. Renders
   * nothing at all until the deployment has been told an entity and a contact:
   * a heading with blanks under it looks like a disclosure while disclosing
   * nothing.
   */
  private renderLegal() {
    const l = this.legal;
    if (!l.configured) return nothing;
    const links: TemplateResult[] = [];
    if (l.privacyUrl) links.push(html`<a href=${l.privacyUrl} rel="noopener">${t('legal.privacy')}</a>`);
    if (l.termsUrl) links.push(html`<a href=${l.termsUrl} rel="noopener">${t('legal.terms')}</a>`);
    if (l.contact) links.push(html`<a href=${`mailto:${l.contact}`}>${t('legal.contact')}</a>`);
    return html`
      <footer class="legal">
        <div class="legal-who">
          ${l.parent ? html`<span>${t('legal.partOf', { parent: l.parent })}</span> · ` : nothing}
          <span>${l.entity}</span>
        </div>
        ${l.address ? html`<div class="legal-line">${l.address}</div>` : nothing}
        ${l.regNo || l.vat || l.jurisdiction
          ? html`<div class="legal-line">${[l.regNo, l.vat, l.jurisdiction].filter(Boolean).join(' · ')}</div>`
          : nothing}
        ${links.length ? html`<div class="legal-links">${links}</div>` : nothing}
      </footer>`;
  }

  /** Leave the shared card and start building your own. */
  private startMyCard = () => {
    location.href = '/';
  };

  // ── "Keep your cards": sign in from the tapped card, with no card of your own ──
  @state() private keepContact = '';
  @state() private keepOtpId = '';
  @state() private keepCode = '';
  @state() private keepBusy = false;
  @state() private keepError = '';
  /** The normalised destination, echoed back so a typo is obvious immediately.
   *  This is the caller's own input — it discloses nothing. */
  @state() private keepSentTo = '';

  /**
   * Signed in → their collection, with the tapped one already above.
   * Not signed in → the field that makes "saved to your cards" true.
   *
   * Nothing about whether the number is already known is shown BEFORE the code
   * is accepted. A public page that answered differently for a registered
   * number would be a way to test which numbers are registered, which is the
   * same reason resolve and claim return one generic 404.
   */
  private renderKeepCards() {
    const view = keepView({
      loaded: this.loaded, signedIn: signedIn(),
      collected: this.collected.length, otpId: this.keepOtpId,
    });
    if (view === 'none') return nothing;
    if (view === 'collection') {
      // Their COLLECTION, not their own cards — "my card" is already a
      // destination in the nav above, so repeating it here would spend the
      // space on the one thing they can always reach. The tapped card is
      // directly above and has just joined this list.
      return html`
        <div class="yours">
          <div class="yours-h">${t('recv.yourCards')}</div>
          ${this.collected.map((m) => html`
            <a class="yours-row" href=${`/${m.publicId ?? ''}`}>
              ${icon('badge')}<span>${cardLabel(m) || m.name || ''}</span>
            </a>`)}
        </div>`;
    }
    if (view === 'code') {
      return html`
        <div class="keep">
          <div class="keep-h">${t('recv.keepSent', { where: this.keepSentTo })}</div>
          <div class="keep-row">
            <input class="keep-in" .value=${this.keepCode} inputmode="numeric"
              autocomplete="one-time-code" maxlength="8"
              @input=${(e: Event) => { this.keepCode = (e.target as HTMLInputElement).value; }} />
            <md-filled-button ?disabled=${this.keepBusy || !this.keepCode.trim()}
              @click=${this.confirmKeep}>${t('recv.keepConfirm')}</md-filled-button>
          </div>
          ${this.keepError ? html`<div class="keep-err">${this.keepError}</div>` : nothing}
        </div>`;
    }
    return html`
      <div class="keep">
        <div class="keep-h">${t('recv.keepHead')}</div>
        <div class="keep-b">${t('recv.keepHint')}</div>
        <div class="keep-row">
          <input class="keep-in" .value=${this.keepContact} type="tel" inputmode="tel"
            autocomplete="tel" placeholder=${t('recv.keepPh')}
            @input=${(e: Event) => { this.keepContact = (e.target as HTMLInputElement).value; }} />
          <md-filled-button ?disabled=${this.keepBusy || !this.keepContact.trim()}
            @click=${this.startKeep}>${t('recv.keepCta')}</md-filled-button>
        </div>
        ${this.keepError ? html`<div class="keep-err">${this.keepError}</div>` : nothing}
      </div>`;
  }

  private startKeep = async () => {
    this.keepBusy = true;
    this.keepError = '';
    try {
      const { signInStart } = await import('./lib/api');
      const res = await signInStart(this.keepContact);
      this.keepOtpId = res.otpId;
      this.keepSentTo = this.keepContact.trim();
      this.keepCode = '';
    } catch {
      // The server answers every failure identically on purpose, so there is
      // nothing more specific to say here than "that is not usable".
      this.keepError = t('recv.keepFail');
    } finally {
      this.keepBusy = false;
    }
  };

  private confirmKeep = async () => {
    this.keepBusy = true;
    this.keepError = '';
    try {
      const { authWithOtp } = await import('./lib/api');
      await authWithOtp('users', this.keepOtpId, this.keepCode.trim());
      // Signed in. Show it NOW and let the cards arrive — the person has already
      // waited for a code, and blocking the whole view on one more round trip is
      // the queue they should not be put in.
      this.keepOtpId = '';
      this.keepCode = '';
      this.keepContact = '';
      void this.syncAfterKeep();
    } catch {
      this.keepError = t('claim.badCode');
    } finally {
      this.keepBusy = false;
    }
  };

  /** Pull the identity's cards in the background, after the code is accepted. */
  private async syncAfterKeep() {
    try {
      const { syncFromAccount } = await import('./lib/sync');
      await syncFromAccount();
      await this.load();
    } catch {
      /* offline — the session still stands, and sync retries on next launch */
    }
  }

  /** Whether the contact was just handed to the OS, so the button can say so. */
  @state() private savedContact = false;
  private savedContactTimer = 0;

  /**
   * Hand this card to the phone's own contacts.
   *
   * Entirely local — the file is built here and passed to the OS, so it works
   * offline and tells no server that it happened, which is the same promise the
   * rest of the receiver path makes.
   */
  private saveContact = (c: Card) => {
    downloadVCard({
      name: c.name,
      title: c.title,
      company: c.company,
      phone: c.phone,
      email: c.email,
      website: c.website,
      address: c.address,
      photo: c.photo,
      url: c.publicId ? shareUrl(c.publicId) : '',
      extraPhones: c.extraPhones,
      extraEmails: c.extraEmails,
    });
    this.savedContact = true;
    clearTimeout(this.savedContactTimer);
    this.savedContactTimer = window.setTimeout(() => { this.savedContact = false; }, 4000);
  };

  /**
   * One-click invitation, sent by the PERSON — never by the server.
   *
   * Public data does not grant permission to message someone: a platform-sent
   * "somebody added your card" is platform marketing and needs consent (US TCPA,
   * EU PECR). Handing the message to the sender's own SMS/mail app makes it what
   * it actually is — one person passing another their card. It also needs no SMS
   * provider and costs nothing.
   *
   * Kept deliberately short; the link carries the id that does the real work.
   */
  private invite = (c: Card) => {
    // The link must point at THEIR card, not ours. That is what makes the
    // invitation do anything: they open it, tap "this is my card", get a code
    // on the number or address already printed on it, and are signed in — with
    // the card, and whoever has collected them, already there. A link to our
    // own card would just show them a stranger's details and end.
    const theirs = c.publicId ? shareUrl(c.publicId) : '';
    if (!theirs) return; // nothing to claim yet; nothing worth sending
    // Composed in the SENDER's language: they are the author, and their SMS app
    // shows the text before it goes, so they can change it.
    const text = t('invite.body', { url: theirs });
    if (c.phone) {
      location.href = `sms:${c.phone.replace(/[^\d+]/g, '')}?&body=${encodeURIComponent(text)}`;
    } else if (c.email) {
      location.href = `mailto:${encodeURIComponent(c.email)}?subject=${encodeURIComponent(t('invite.subject'))}&body=${encodeURIComponent(text)}`;
    } else if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
      void navigator.share({ text }).catch(() => {});
    }
  };

  private onClaimed = async () => {
    this.claimingCardId = '';
    this.claimIsNew = false;
    // The card is now bound to an identity, so pull everything that identity
    // owns onto this device. This is what makes a second phone look like the
    // first — and it never moves a public id, so programmed tags keep working.
    try {
      await syncFromAccount();
    } catch { /* offline: the claim still stands, sync retries on next load */ }
    [this.my, this.mine, this.collected] = await Promise.all([myCard(), listMyCards<Card>(), collectedCards()]);
    if (this.route.view === 'profile') this.profile = await publicCard(this.route.id);
    if (this.headingForWork) {
      this.headingForWork = false;
      this.scrollToWork();
    }
  };

}

declare global {
  interface HTMLElementTagNameMap { 'bc-app': BcApp; }
}
